import { Injectable } from '@angular/core';
import { IDomainFeatureFlags, IOrgFeatureFlags } from '../../config/tenant.interface';
import { TenantService } from './tenant.service';
import { StorageService } from '../../services/storage.service';

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  constructor(
    private tenantService: TenantService,
    private storageService: StorageService,
  ) {}

  // ── Domain-level flags (from tenant config, known at startup) ──────────────

  isDomainFeatureEnabled(flag: keyof IDomainFeatureFlags): boolean {
    return this.tenantService.getConfig()?.features?.[flag] ?? false;
  }

  /** Resolve a menu/route permission from the current role's API permissions. */
  hasReadPermission(permissionName: string, allowMissingPermission = false): boolean {
    const permissions = this.storageService.getAccountPermissions();
    if (!permissions) {
      console.log(
        `[Permission check] role=${this.storageService.getRole() ?? 'unknown'}, permission=${permissionName}, read=false (no API permissions loaded)`,
      );
      return allowMissingPermission;
    }

    const currentRole = this.storageService.getRole();
    const rolePermissions = permissions.find(
      (entry: any) => entry?.group?.name === currentRole,
    );
    const normalizedName = this.normalizePermissionName(permissionName);
    const permission = Object.entries(rolePermissions?.permissions ?? {}).find(
      ([name]) => this.normalizePermissionName(name) === normalizedName,
    );
    const canRead = rolePermissions
      ? permission === undefined || (permission[1] as any)?.read === true
      : allowMissingPermission;
    console.log(
      `[Permission check] role=${currentRole ?? 'unknown'}, permission=${permissionName}, read=${canRead}`,
    );
    return canRead;
  }

  isPreProcurementEnabled(): boolean {
    return this.hasReadPermission('Pre Procurement', true);
  }

  private normalizePermissionName(name: string): string {
    return name.toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  // ── Org-level flags (from StorageService, available after login) ───────────

  getOrgFlags(): IOrgFeatureFlags {
    return {
      hdhoFields: this.storageService.getOrganizationId() === 'HDHO',
      abclMidMarket: this.storageService.getAbclMidMarket(),
      dropboxEnabled: this.storageService.getDropBoxEnabled(),
      cloudSign: this.storageService.getCloudSign(),
      defaultEsign: this.storageService.getInfiniteCorp(),
      estampDefault: !!this.storageService.getEstampDefault(),
      esignDefault: !!this.storageService.getEsignDefault(),
      ssoDefault: !!this.storageService.getSsoDefault(),
      authorisedSignature: this.storageService.getAuthorisedSignature(),
    };
  }

  isOrgFeatureEnabled(flag: keyof IOrgFeatureFlags): boolean {
    return this.getOrgFlags()[flag] ?? false;
  }

  // ── Unified check (domain first, then org) ─────────────────────────────────

  isEnabled(flag: keyof IDomainFeatureFlags | keyof IOrgFeatureFlags): boolean {
    if (flag in (this.tenantService.getConfig()?.features ?? {})) {
      return this.isDomainFeatureEnabled(flag as keyof IDomainFeatureFlags);
    }
    return this.isOrgFeatureEnabled(flag as keyof IOrgFeatureFlags);
  }

  // ── Convenience helpers (replaces scattered orgId === 'HDHO' checks) ────────

  showHdhoFields(): boolean {
    return this.isOrgFeatureEnabled('hdhoFields');
  }

  isAbclMidMarket(): boolean {
    return this.isOrgFeatureEnabled('abclMidMarket');
  }
}
